import { useMemo } from "react";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { ReservationStateEnum, type Reservation } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { PillTone } from "@tenant/ui";
import { ReservationStatusPill } from "../ReservationStatusPill";
import type { TripHeaderProps } from "./types";

interface UseHotelTripHeaderProps {
  reservation: Reservation;
  className?: string;
  isCfarExercised?: boolean;
}

export const useHotelTripHeader = ({
  reservation,
  className,
  isCfarExercised,
}: UseHotelTripHeaderProps): TripHeaderProps => {
  const { t } = useI18nContext();

  const pillContent = useMemo(() => {
    if (isCfarExercised) {
      return {
        status: t("core-hotels.reservationState.Cancelled"),
        pillTone: PillTone.error,
      };
    }

    switch (reservation.state.ReservationState) {
      case ReservationStateEnum.CancellationFailure:
        return {
          status: t("core-hotels.reservationState.CancellationFailure"),
          pillTone: PillTone.error,
        };
      case ReservationStateEnum.Cancelled:
        return {
          status: t("core-hotels.reservationState.Cancelled"),
          pillTone: PillTone.error,
        };
      case ReservationStateEnum.Confirmed:
        return {
          status: t("core-hotels.reservationState.Confirmed"),
          pillTone: PillTone.success,
        };
      case ReservationStateEnum.Pending:
        return {
          status: t("core-hotels.reservationState.Pending"),
          pillTone: PillTone.neutral,
        };
      default:
        return ensureExhaustive(reservation.state, {
          status: "",
          pillTone: PillTone.neutral,
        });
    }
  }, [isCfarExercised, reservation.state, t]);

  return useMemo(() => {
    const { status, pillTone } = pillContent;

    const statusPill = (
      <ReservationStatusPill tone={pillTone}>{status}</ReservationStatusPill>
    );

    return {
      className,
      statusPill,
      title: t("hotelTrips.yourStayIn", {
        city: reservation.lodgingData?.city ?? "",
      }),
      confirmationCode: reservation.reservationId,
      confirmationLabel: t("hotelTrips.confirmationCode"),
    };
  }, [reservation, className, pillContent, t]);
};
